/**
 * Google Analytics (GA4) for the single-page app.
 *
 * The server-rendered entry points (the platform homepage and the /menu page)
 * already inject the gtag.js snippet into <head> from the platform's configured
 * Measurement ID, so on those loads `window.gtag` exists before React mounts.
 * This module:
 *
 *   1. bootstraps gtag from a build-time id (REACT_APP_GA_ID) *only if* the SSR
 *      head didn't already load it — so client-only routes still get analytics; and
 *   2. sends a `page_view` on every client-side route change (SPA navigations
 *      don't reload the document, so GA would otherwise miss them).
 *
 * It never throws into the app: analytics must never break the UI.
 */

const BUILD_GA_ID = (process.env.REACT_APP_GA_ID || '').trim();

function hasGtag() {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
}

/** Load gtag.js for `id` if it isn't already on the page. */
function bootstrap(id) {
  if (!id || typeof document === 'undefined' || hasGtag()) return;
  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', id, { anonymize_ip: true, send_page_view: false });
  } catch { /* ignore */ }
}

/** Call once on app start. Safe to call when no id is configured. */
export function initGoogleAnalytics() {
  if (BUILD_GA_ID) bootstrap(BUILD_GA_ID);
}

/** Record a virtual page view for the current SPA location. */
export function trackGaPageView(path) {
  if (!hasGtag()) return;
  try {
    window.gtag('event', 'page_view', {
      page_path: path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''),
      page_location: typeof window !== 'undefined' ? window.location.href : undefined,
      page_title: typeof document !== 'undefined' ? document.title : undefined,
    });
  } catch { /* ignore */ }
}
